import type { TooltipProps } from './Tooltip';

/**
 * localStorage에서 툴팁을 본 횟수를 가져옵니다
 * @param storageKey - localStorage 키
 * @returns 툴팁을 본 횟수 (0부터 시작)
 */
const getTooltipVisibleCount = (storageKey: string): number => {
  try {
    const value = localStorage.getItem(storageKey);
    if (value === null) return 0;

    const count = parseInt(value, 10);
    return Number.isNaN(count) ? 0 : count;
  } catch (error) {
    console.warn('Failed to access localStorage:', error);
    return 0;
  }
};

/**
 * localStorage에 툴팁을 본 횟수를 증가시킵니다
 * @param storageKey - localStorage 키
 */
export const incrementTooltipVisibleCount = (storageKey: string): void => {
  try {
    const currentCount = getTooltipVisibleCount(storageKey);
    localStorage.setItem(storageKey, String(currentCount + 1));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
};

/**
 * 툴팁이 최대 표시 횟수에 도달했는지 확인합니다
 * @param storageKey - localStorage 키
 * @param maxCount - 최대 표시 횟수 (기본값: 1)
 * @returns 최대 횟수에 도달했으면 true
 */
export const hasReachedMaxCount = (storageKey: string, maxCount: number = 1): boolean => {
  const count = getTooltipVisibleCount(storageKey);
  return count >= maxCount;
};

export const getArrowStyle = (align: TooltipProps['align']): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
  };

  const positionStyles: Record<TooltipProps['align'], React.CSSProperties> = {
    'top left': { left: '8px', bottom: '-4px' },
    'top center': {
      left: '50%',
      bottom: '-4px',
      transform: 'translateX(-50%)',
    },
    'top right': { right: '8px', bottom: '-4px' },

    'bottom left': { left: '8px', top: '-4px', transform: 'rotate(180deg)' },
    'bottom center': {
      left: '50%',
      top: '-4px',
      transform: 'translateX(-50%) rotate(180deg)',
    },
    'bottom right': { right: '8px', top: '-4px', transform: 'rotate(180deg)' },

    'left top': { right: '-7px', top: '8px', transform: 'rotate(-90deg)' },
    'left center': {
      right: '-7px',
      top: '50%',
      transform: 'translateY(-50%) rotate(-90deg)',
    },
    'left bottom': {
      right: '-7px',
      bottom: '8px',
      transform: 'rotate(-90deg)',
    },

    'right top': { left: '-7px', top: '8px', transform: 'rotate(90deg)' },
    'right center': {
      left: '-7px',
      top: '50%',
      transform: 'translateY(-50%) rotate(90deg)',
    },
    'right bottom': { left: '-7px', bottom: '8px', transform: 'rotate(90deg)' },
  };

  return { ...baseStyle, ...positionStyles[align] };
};
